import React, { useState, useEffect } from 'react';
import { X, Send, MessageSquareWarning } from 'lucide-react';
import api, { formatError } from '../lib/api';
import { toast } from 'sonner';

const QUICK_TEMPLATES = [
  "Quick check-in please — drop an update by EOD.",
  "Hey, this one is overdue. What's blocking you?",
  "Please post your status today.",
  "Reminder: I need an answer on this before tomorrow.",
];

export default function NudgeDialog({ open, recipients = [], onClose, onSent }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) setMessage(''); }, [open]);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error('Write a quick note');
    setSending(true);
    try {
      const { data } = await api.post('/snapshot/nudge', {
        user_ids: recipients.map((r) => r.id),
        message,
      });
      toast.success(`Nudged ${data.sent_to} ${data.sent_to === 1 ? 'person' : 'people'}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-900 shadow-brutal w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="nudge-dialog">
        <div className="flex items-center justify-between border-b border-zinc-200 p-5">
          <div className="flex items-center gap-2">
            <MessageSquareWarning size={20} strokeWidth={2} />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">SEND NUDGE</div>
              <div className="font-display text-2xl font-black tracking-tighter">Quick poke</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-nudge"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">RECIPIENTS · {recipients.length}</div>
            <div className="flex flex-wrap gap-1">
              {recipients.map((r) => (
                <span key={r.id} className="text-xs bg-zinc-100 border border-zinc-300 px-2 py-0.5">{r.name}</span>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Your message</label>
            <textarea
              data-testid="nudge-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Type a quick note. It will land in their notification bell instantly."
              className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2 bg-white resize-none"
            />
            <div className="text-[10px] font-mono text-zinc-400 text-right mt-1">{message.length}/500</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">QUICK TEMPLATES</div>
            <div className="flex flex-wrap gap-1">
              {QUICK_TEMPLATES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setMessage(t)}
                  data-testid={`template-${t.slice(0, 12).replace(/ /g, '-').toLowerCase()}`}
                  className="text-xs border border-zinc-300 px-2 py-1 hover:border-zinc-900 hover:bg-zinc-50 text-left"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="border border-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white transition-colors" data-testid="cancel-nudge">Cancel</button>
            <button type="submit" disabled={sending} className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#002088] disabled:opacity-60 transition-colors" data-testid="send-nudge-btn">
              <Send size={12} /> {sending ? 'Sending...' : 'Send Nudge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
